/**
 * Smart Review offline question cache.
 *
 * The Service Worker periodically prefetches `/api/student/smart-review/preview`
 * (Phase 22) and stores the response in Cache Storage. This module wraps that
 * cache so the React side can:
 *   - check whether cached questions exist
 *   - read them out
 *   - manually refresh while online
 *
 * Falls back to a direct API call when running outside of a SW context.
 */
import { api, API } from "@/lib/api";

const PREFETCH_CACHE = "prepmasters-api-prefetch-v1";
const PREVIEW_URL = `${API}/student/smart-review/preview`;

/** Returns the cached preview JSON or null. */
export async function getCachedPreview() {
  if (!("caches" in window)) return null;
  try {
    const cache = await caches.open(PREFETCH_CACHE);
    const resp = await cache.match(PREVIEW_URL);
    if (!resp) return null;
    return await resp.clone().json();
  } catch { return null; }
}

/** Always returns fresh preview from network, AND warms the cache. */
export async function fetchAndCachePreview(size = 15) {
  const r = await api.get(`/student/smart-review/preview?size=${size}`);
  // Re-cache in browser cache so the offline path picks it up immediately
  if ("caches" in window) {
    try {
      const cache = await caches.open(PREFETCH_CACHE);
      await cache.put(
        PREVIEW_URL,
        new Response(JSON.stringify(r.data), {
          status: 200,
          headers: { "Content-Type": "application/json" },
        })
      );
    } catch { /* noop */ }
  }
  return r.data;
}

/** Returns either fresh-from-network preview, or cached if offline/network fails. */
export async function getPreviewWithFallback(size = 15) {
  if (typeof navigator !== "undefined" && navigator.onLine !== false) {
    try { return { data: await fetchAndCachePreview(size), source: "network" }; } catch { /* fall through */ }
  }
  const cached = await getCachedPreview();
  if (cached) return { data: cached, source: "cache" };
  return { data: null, source: "none" };
}
